/**
 * The referral dashboard surface — a display-only card of your invite link,
 * invited/qualified/earned stats, and the referral history. The api is the
 * sole authority on qualification + payout math (CLAUDE.md: cards/modals are
 * display-only); this component only formats and lays them out, and copies
 * the link to the clipboard (a client-only convenience, no domain logic).
 * Loads once on mount via the injected {@link ReferralStore}.
 */
import { Button, Spinner, useT } from '@4f/ui';
import { createMemo, createSignal, For, type JSX, onMount, Show } from 'solid-js';
import type { ReferredUserView } from '../../api/types.ts';
import type { ReferralStore } from '../../stores/referral.ts';
import { buildReferralLink, microsToCredits, referralRewardLines } from './referral-model.ts';

export interface ReferralSurfaceProps {
  readonly store: ReferralStore;
  /** The app's own origin (`location.origin`) — the shell owns the browser global. */
  readonly origin: string;
}

export function ReferralSurface(props: ReferralSurfaceProps): JSX.Element {
  const { t } = useT();
  const [copied, setCopied] = createSignal(false);

  onMount(() => {
    void props.store.load();
  });

  const link = createMemo(() => {
    const summary = props.store.summary();
    return summary ? buildReferralLink(props.origin, summary.referralCode) : '';
  });

  // Reward copy interpolated from the server-authoritative summary — built via
  // the pure `referralRewardLines` so the exact output is unit-tested (no DOM).
  const rewards = createMemo(() => {
    const summary = props.store.summary();
    return summary ? referralRewardLines(t, summary) : null;
  });

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(link());
      setCopied(true);
    } catch {
      // Clipboard access denied/unavailable — the link is still selectable text.
      setCopied(false);
    }
  };

  return (
    <div class="f4-referral">
      <Show when={props.store.loading() && !props.store.summary()}>
        <Spinner label={t('referral.title')} />
      </Show>
      <Show when={props.store.error()}>
        {(error) => (
          <p class="f4-genui__error" role="alert">
            {error().message}
          </p>
        )}
      </Show>
      <Show when={props.store.summary()}>
        {(summary) => (
          <>
            <section class="f4-referral__link">
              <label class="f4-referral__linkLabel" for="f4-referral-link-input">
                {t('referral.link.label')}
              </label>
              <div class="f4-referral__linkRow">
                <input
                  id="f4-referral-link-input"
                  class="f4-referral__linkInput"
                  type="text"
                  readonly
                  value={link()}
                  onFocus={(event) => event.currentTarget.select()}
                />
                <Button type="button" variant="primary" size="sm" onClick={() => void copyLink()}>
                  {copied() ? t('referral.link.copied') : t('referral.link.copy')}
                </Button>
              </div>
            </section>

            <section class="f4-referral__stats" aria-label={t('referral.title')}>
              <p class="f4-referral__stat">
                {t('referral.stats.invited', { count: summary().invitedCount })}
              </p>
              <p class="f4-referral__stat">
                {t('referral.stats.joined', { count: summary().qualifiedCount })}
              </p>
              <p class="f4-referral__stat">
                {t('referral.stats.earned', { amount: microsToCredits(summary().earnedMicros) })}
              </p>
            </section>

            <section class="f4-referral__rewards">
              <p class="f4-referral__rewardLine">{rewards()?.signupBonus}</p>
              <p class="f4-referral__rewardLine">{rewards()?.spendBack}</p>
            </section>

            <ReferredList referred={summary().referred} />
          </>
        )}
      </Show>
    </div>
  );
}

function ReferredList(props: { readonly referred: readonly ReferredUserView[] }): JSX.Element {
  const { t } = useT();
  return (
    <section class="f4-referral__history" aria-label={t('referral.title')}>
      <Show
        when={props.referred.length > 0}
        fallback={<p class="f4-referral__empty">{t('referral.empty')}</p>}
      >
        <ul class="f4-referral__list">
          <For each={props.referred}>
            {(entry) => (
              <li class="f4-referral__row">
                <span class={`f4-referral__status f4-referral__status--${entry.status}`}>
                  {t(`referral.status.${entry.status}`)}
                </span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </section>
  );
}
